// E08-t2 — the Dashboards page's TRANSPORT: the types the page is written
// against, and the one honest fact about what the control plane publishes today.
//
// The page NEVER calls global fetch. It takes a `DashboardsClient` and asks it
// two questions — what does this KB publish, and save this composition — which
// is what lets the component contract run with no network, no msw and no
// backend, and what lets the app hand it the real transport unchanged.
//
// THE SHAPES ARE THE CONTROL PLANE'S, NOT THIS FILE'S:
//   `format`   is the kb_record MAP KEY — git-host/src/render.ts is explicit
//              that this, never the pushed definition's internal `format.name`,
//              is the name that appears in the manifest and in the URL.
//   `primary`  is kb_record's designated primary format.
//   `url`      is git-host/src/serve.ts SERVE_ROUTE — /:org/:kb/dashboard/:format
//              — and is supplied BY the control plane rather than assembled
//              here, so a url shape change is a server change, not a client one.
//   `views`    is 0015 Composable View Definitions: "a composition is a
//              versioned list of view references", so it is an ORDERED list of
//              view IDS and nothing else. There is no view BODY here, and there
//              must never be: Forge f2 forbids a free-form editor, so the write
//              this page can make is picking and ordering STORED views.
//   `report`   is E32-t5's surface family (decision 0019): the format's report
//              surface when it owns one — the serve url, catalog.ts's own
//              string — and null when it does not.
//
// ---------------------------------------------------------------------------
// THE GAP, STATED RATHER THAN FAKED (read before wiring anything here)
// ---------------------------------------------------------------------------
// git-host publishes the render MANIFEST — one entry per format that owns a
// dashboard, with title, primary, commit, renderedAt and engine
// (git-host/src/render.ts RenderManifest) — to the Artifact Store at the R2 key
// `dashboard/{org}/{kb}.json`. NOTHING SERVES IT OVER HTTP. The routes that
// exist are the render itself (/:org/:kb/dashboard/:format[/payload|/doc/…],
// git-host/src/serve.ts) and the JSON control surface /api/:org/:kb/{refs,
// commands,usage,command,ledger,delta} (git-host/src/index.ts). There is no
// GET /api/:org/:kb/dashboards, and there is no composition WRITE endpoint at
// all. E05's note made that conditional on the engine pin reaching `include:`
// support; the pin is now 0.8.0 and `include:` is live, so THE ENGINE IS NO
// LONGER WHAT IS MISSING — the control-plane endpoints are.
//
// So this module does NOT invent one. `createDashboardsClient` returns a client
// whose two methods refuse with `MissingControlPlaneEndpoint`, naming the
// endpoint that has to exist and the manifest that already holds the answer.
// The page renders that refusal as a sentence a reader can act on. When the
// endpoint lands, only the two bodies below change — no page, no test, no type.

import { appFetch } from './transport';
/** git-host/src/render.ts: the four states a listed composition can be in. */
export type RenderStatus = 'rendered' | 'stale' | 'never' | 'failed';

/** One stored view of ONE format — 0015: reuse follows the format. */
export interface StoredView {
  id: string;
  title: string;
  /**
   * E32-t5 — the view's SOURCE FILE (docujoint 0.14.0: one view per file under
   * views/, the id IS the filename slug). A view's identity is its file, so the
   * listing says which one; null on an answer that predates the field (the
   * catalog's payload fallback), which changes nothing about id or title.
   */
  file: string | null;
}

/** One dashboard/composite of one format, as the control plane describes it. */
export interface Composition {
  /** stable composition id within the format */
  id: string;
  /** the dashboard definition's own title */
  title: string;
  status: RenderStatus;
  /** the stable URL (SERVE_ROUTE), or null while nothing is published */
  url: string | null;
  /** the commit the render was computed from */
  commit: string | null;
  /** ISO */
  renderedAt: string | null;
  /** 0015: an ORDERED list of view references */
  views: string[];
}

/** Everything one format owns. A format that owns no dashboard is ABSENT. */
export interface FormatDashboards {
  format: string;
  primary: boolean;
  compositions: Composition[];
  storedViews: StoredView[];
  /**
   * E32-t5 — the format's REPORT surface (decision 0019's surface family): a
   * format may own a report definition beside its dashboard, baked as ONE
   * artifact and served at the reserved segment. The url is git-host/src/
   * catalog.ts's own string — /:org/:kb/dashboard/:format/report — never
   * assembled here, for the same reason `url` above never is. `null` is the
   * catalog's word for "no report surface", and this client keeps it: absence
   * is an answer the page renders as nothing, not an error.
   */
  report: { url: string } | null;
  /**
   * E40-t2 — the format's FORM surface (the intake): the serve url when the KB
   * publishes one, `null` when it does not. Read exactly like `report` above —
   * the control plane's own string, never assembled here — and `null` for the
   * same reason: "this format owns no intake" is an answer the page renders as
   * nothing, not a missing key it has to guess about.
   */
  form: { url: string } | null;
}

/** A KB's published dashboards, grouped per format. */
export interface KbDashboards {
  org: string;
  kb: string;
  formats: FormatDashboards[];
}

/** The composition write: ids, in the order the maintainer put them in. */
export interface SaveCompositionInput {
  org: string;
  kb: string;
  format: string;
  composition: string;
  views: string[];
}

/** The handoff OUT of this page (Dashboards f3 → Agent Chat f3 → Dashboard On
 *  Demand f1, whose mission is the INTENT plus the AUDIENCE). */
export interface RequestViewHandoff {
  org: string;
  kb: string;
  format: string;
  intent: string;
  audience: string;
}

/** The two questions the page asks. Injected, so the page holds no fetch. */
export interface DashboardsClient {
  listDashboards(org: string, kb: string): Promise<KbDashboards>;
  saveComposition(input: SaveCompositionInput): Promise<void>;
}

/**
 * A surface the control plane does not publish yet. Thrown rather than
 * stubbed: a fabricated listing would make the page look finished and make the
 * end-to-end axis lie about a pipeline that does not exist.
 */
export class MissingControlPlaneEndpoint extends Error {
  readonly endpoint: string;

  constructor(endpoint: string, detail: string) {
    super(`${endpoint} does not exist yet — ${detail}`);
    this.name = 'MissingControlPlaneEndpoint';
    this.endpoint = endpoint;
  }
}

/** The status words git-host answers with, and the only ones this app accepts. */
const STATUSES: readonly RenderStatus[] = ['rendered', 'stale', 'never', 'failed'];
const asStatus = (value: unknown): RenderStatus =>
  STATUSES.includes(value as RenderStatus) ? (value as RenderStatus) : 'never';

const str = (value: unknown): string | null => (typeof value === 'string' && value ? value : null);

/**
 * The app's transport.
 *
 * THE READ IS REAL NOW. git-host/src/catalog.ts projects the render manifest an
 * accepted push already published onto GET /api/:org/:kb/dashboards, so this
 * asks for it — credentials 'same-origin', because the members-only session
 * travels as the `dj_session` cookie (platform/src/session-store.ts) and the app
 * and the Worker are ONE ORIGIN in the browser (see vite.config.ts). Nothing is
 * assembled here: the format names, the primary flag, the render status and the
 * STABLE url are the control plane's own words, read out of its answer.
 *
 * THE WRITE STILL REFUSES, and still says exactly why — see below.
 */
export function createDashboardsClient(fetchImpl: typeof fetch = appFetch): DashboardsClient {
  return {
    async listDashboards(org: string, kb: string): Promise<KbDashboards> {
      const path = `/api/${encodeURIComponent(org)}/${encodeURIComponent(kb)}/dashboards`;
      const response = await fetchImpl(path, { credentials: 'same-origin' });
      if (!response.ok) {
        // A refusal is an ordinary answer with the guard NAMED (guards.ts), so
        // it travels up in those words rather than flattened into "failed".
        const refusal = (await response.json().catch(() => ({}))) as {
          guard?: string;
          reason?: string;
        };
        throw new Error(
          `${refusal.guard ? `${refusal.guard}: ` : ''}` +
            `${refusal.reason ?? `GET ${path} answered ${response.status}`}`,
        );
      }
      const body = (await response.json()) as { formats?: Array<Record<string, unknown>> };
      const formats: FormatDashboards[] = [];
      for (const entry of body.formats ?? []) {
        const format = str(entry?.format);
        if (!format) continue;
        const storedViews: StoredView[] = [];
        for (const view of (entry.storedViews as Array<Record<string, unknown>>) ?? []) {
          const id = str(view?.id);
          if (id) storedViews.push({ id, title: str(view?.title) ?? id, file: str(view?.file) });
        }
        const compositions: Composition[] = [];
        for (const raw of (entry.compositions as Array<Record<string, unknown>>) ?? []) {
          const id = str(raw?.id);
          if (!id) continue;
          const views = Array.isArray(raw.views) ? (raw.views as unknown[]) : [];
          compositions.push({
            id,
            title: str(raw.title) ?? id,
            status: asStatus(raw.status),
            url: str(raw.url),
            commit: str(raw.commit),
            renderedAt: str(raw.renderedAt),
            views: views.filter((view): view is string => typeof view === 'string'),
          });
        }
        // E32-t5: the report surface, read out of the catalog's answer the same
        // way every url in this file is — its word, or null. A malformed entry
        // (report without a url string) reads as no report rather than a crash:
        // the surface family must never cost the LISTING.
        const reportUrl = str((entry.report as Record<string, unknown> | null | undefined)?.url);
        // E40-t2: the form surface, read the same way and just as defensively —
        // an answer that predates the field, or a malformed one, is no intake.
        const formUrl = str((entry.form as Record<string, unknown> | null | undefined)?.url);
        formats.push({
          format,
          primary: entry.primary === true,
          compositions,
          storedViews,
          report: reportUrl ? { url: reportUrl } : null,
          form: formUrl ? { url: formUrl } : null,
        });
      }
      return { org, kb, formats };
    },
    async saveComposition(input: SaveCompositionInput): Promise<void> {
      throw new MissingControlPlaneEndpoint(
        `PUT /api/${input.org}/${input.kb}/dashboards/${input.format}/${input.composition}`,
        'the engine side of composition is in place — the Worker is pinned to docujoint 0.8.0 ' +
          'and renders `include:` — but no endpoint accepts a composition to store, so there ' +
          'is nowhere to write one yet (git-host/src/render.ts)',
      );
    },
  };
}
